import React from 'react';
import classNames from 'classnames';
import { Text } from '@/components/atoms/AppText';
import { APP_BG, APP_BORDER, APP_TEXT } from '@/constant/theme/colors.constant';

export interface KpiCardProps {
  /** Short uppercase card label */
  label: string;
  /** Icon element — sized to ~20px by the card wrapper */
  icon: React.ReactNode;
  /** Gradient start colour (CSS colour string) */
  gradientFrom: string;
  /** Gradient end colour (CSS colour string) */
  gradientTo: string;
  /** Optional glow colour for the icon bubble (CSS colour string) */
  glowColor?: string;
  /**
   * Animated status dot shown beside the primary value.
   * `"online"` → green pinging dot, `"offline"` → static red dot.
   */
  statusDot?: 'online' | 'offline' | 'warning' | 'info' | null;
  /** Optional badge rendered below the card label (e.g. PillCell status) */
  statusBadge?: React.ReactNode;
  /** Main large metric value */
  primary: React.ReactNode;
  /** Tailwind text-colour class applied to the primary value */
  primaryColor?: string;
  /** AppText color token applied to the primary value (overrides primaryColor) */
  primaryColorToken?: React.ComponentProps<typeof Text>['color'];
  /** Muted line below the primary value */
  secondary?: React.ReactNode;
  /** Footer left side — small icon label */
  sub?: string;
  /** Icon for the sub label */
  subIcon?: React.ReactNode;
  /** Arbitrary slot between primary and footer (progress bar, ring, etc.) */
  extra?: React.ReactNode;
  /** Left slot in the footer row */
  footerLeft?: React.ReactNode;
  /** Right slot in the footer row (e.g. status badge chip) */
  footerRight?: React.ReactNode;
}

/**
 * KpiCard — modern KPI card with a coloured gradient top bar, icon bubble
 * with glow, status dot / badge, primary metric, optional extra slot and
 * a footer row for supplementary info.
 *
 * Designed for use in SmartCooler device views and any device-detail page.
 *
 * Usage:
 * ```tsx
 * <KpiCard
 *   label="Connection"
 *   icon={<LuWifi />}
 *   gradientFrom="#10b981"
 *   gradientTo="#34d399"
 *   glowColor="#10b981"
 *   statusDot="online"
 *   primary="Live"
 *   primaryColor="text-emerald-600 dark:text-emerald-400"
 *   sub="2 hours ago"
 *   subIcon={<LuClock />}
 * />
 * ```
 */
export const KpiCard = ({
  label,
  icon,
  gradientFrom,
  gradientTo,
  glowColor,
  statusDot,
  statusBadge,
  primary,
  primaryColor,
  primaryColorToken,
  secondary,
  sub,
  subIcon,
  extra,
  footerLeft,
  footerRight,
}: KpiCardProps) => {
  return (
    <div
      className={classNames(
        'relative flex flex-col overflow-hidden group',
        'transition-all duration-300',
        APP_BG.base,
      )}
    >
      {/* Gradient top bar */}
      <div
        className="h-[3px] w-full shrink-0 transition-all duration-300 group-hover:h-[4px]"
        style={{
          background: `linear-gradient(to right, ${gradientFrom}, ${gradientTo})`,
        }}
      />

      {/* Subtle gradient wash behind content */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.035] dark:opacity-[0.07] transition-opacity duration-300 group-hover:opacity-[0.06] dark:group-hover:opacity-[0.12]"
        style={{
          background: `linear-gradient(135deg, ${gradientFrom} 0%, transparent 60%)`,
        }}
      />

      <div className="relative flex flex-col flex-1 px-5 pt-4 pb-4 min-h-[148px]">
        {/* Header row: label + icon bubble */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex flex-col gap-1">
            <Text
              as="span"
              size="xs"
              color="muted"
              className="uppercase tracking-[0.12em] font-bold"
            >
              {label}
            </Text>
            {statusBadge && <div>{statusBadge}</div>}
          </div>

          {/* Icon bubble with glow */}
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl shadow-sm transition-transform duration-200 group-hover:scale-110"
            style={{
              background: `linear-gradient(135deg, ${gradientFrom}22, ${gradientTo}33)`,
              border: `1px solid ${gradientFrom}30`,
              boxShadow: glowColor ? `0 0 14px ${glowColor}30` : undefined,
            }}
          >
            <span
              style={{ color: gradientFrom }}
              className="[&>svg]:w-5 [&>svg]:h-5"
            >
              {icon}
            </span>
          </div>
        </div>

        {/* Primary value */}
        <div className="flex items-center gap-2.5 mb-1">
          {statusDot === 'online' && (
            <span className="relative flex h-3 w-3 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-emerald-400 dark:bg-emerald-300" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500 dark:bg-emerald-400" />
            </span>
          )}
          {statusDot === 'offline' && (
            <span className="relative flex h-3 w-3 shrink-0 rounded-full bg-red-500 dark:bg-red-400" />
          )}
          {statusDot === 'warning' && (
            <span className="relative flex h-3 w-3 shrink-0 rounded-full bg-amber-500 dark:bg-amber-400" />
          )}
          {statusDot === 'info' && (
            <span className="relative flex h-3 w-3 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-blue-400 dark:bg-blue-300" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500 dark:bg-blue-400" />
            </span>
          )}
          <Text
            as="p"
            size="4xl"
            weight="bold"
            color={primaryColorToken ?? 'default'}
            className={classNames(
              'leading-none tabular-nums tracking-tight',
              !primaryColorToken ? (primaryColor ?? '') : '',
            )}
          >
            {primary}
          </Text>
        </div>

        {/* Secondary info */}
        {secondary && (
          <Text
            as="p"
            size="xs"
            color="muted"
            className="font-medium truncate mt-0.5"
          >
            {secondary}
          </Text>
        )}

        {/* Extra slot (progress bar, ring, etc.) */}
        {extra && <div className="mt-3">{extra}</div>}

        {/* Footer */}
        <div
          className={classNames(
            'mt-auto pt-2.5 flex items-center justify-between gap-2 border-t',
            APP_BORDER.filterRow,
          )}
        >
          <div className="flex items-center gap-1.5 min-w-0">
            {subIcon && (
              <span
                className={`shrink-0 ${APP_TEXT.subtle} [&>svg]:w-3 [&>svg]:h-3`}
              >
                {subIcon}
              </span>
            )}
            {sub && (
              <Text as="p" size="xs" color="subtle" className="truncate">
                {sub}
              </Text>
            )}
            {footerLeft && <div>{footerLeft}</div>}
          </div>
          {footerRight && <div className="shrink-0">{footerRight}</div>}
        </div>
      </div>
    </div>
  );
};
